import { filter, map } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { selectLotRoutingOutcome } from './selectors';
import LotNavigationAction from '../enums/lotNavigationAction';

/**
 * Reacts to lotSelected: asks selectLotRoutingOutcome for the full navigation decision for the
 * clicked lot and dispatches the matching action — this epic makes no routing judgment of its
 * own, it only translates a LotNavigationAction.NavigateTo (with `view`/`context`) or
 * NavigateBack outcome into `navigateTo(...)` / `navigateBack()`; `null` means nothing to
 * dispatch. See selectLotRoutingOutcome (redux/selectors.js) for why a non-qualifying lot click
 * can still resolve to NavigateBack instead of a true no-op (NHS20-8166).
 *
 * @param {object} params
 * @param {object} params.dataActions - Actions from the feature data slice instance
 * @param {boolean} params.isMobile - device flag, captured once at epic-creation time (same
 *   signal drawer.plugin.js / cardLotHighlight.plugin.js already gate themselves on) and passed
 *   straight through to selectLotRoutingOutcome on every lot click.
 * @returns {import('redux-observable').Epic}
 */
function createLotSelectedEpic({ dataActions, isMobile }) {
  return (action$, state$) => action$.pipe(
    ofType(dataActions.lotSelected.type),
    map(({ payload: lotId }) => selectLotRoutingOutcome(state$.value, lotId, isMobile)),
    filter(Boolean),
    map((outcome) => {
      if (outcome.type === LotNavigationAction.NavigateTo.toString()) {
        return dataActions.navigateTo({ view: outcome.view, context: outcome.context });
      }
      if (outcome.type === LotNavigationAction.NavigateBack.toString()) {
        return dataActions.navigateBack();
      }
      // Unrecognized type (e.g. a future LotNavigationAction value this epic doesn't handle
      // yet) — drop it rather than guessing, so it never dispatches navigateTo with
      // undefined view/context.
      return null;
    }),
    filter(Boolean),
  );
}

export default createLotSelectedEpic;
